import moment from 'moment'

/**
 * Type Item représentant un élément de dictionnaire (exactement comme dans Epic/Lot.js)
 * @typedef {Object} Item
 * @property {string} key - La clé de l'élément
 * @property {string} value - La valeur de l'élément
 */

/**
 * Reproduction exacte de la fonction itemValue de Epic/Lot.js
 * string => Item[] => string
 * Supporte la syntaxe curryfiée: itemValue(key)(items) ET itemValue(key, items)
 */
export const itemValue = (key, ...args) => {
  // Si appelé avec un seul argument, retourner une fonction curryfiée
  if (args.length === 0) {
    return (items) => itemValue(key, items)
  }
  
  const items = args[0]
  
  if (!key || !items) return ''
  
  // Si items est un tableau d'objets { key, value } (format de l'ancienne app)
  if (Array.isArray(items)) {
    const found = items.find(item => item.key === key)
    return found ? found.value : ''
  }
  
  // Si items est un objet clé-valeur (format actuel)
  if (typeof items === 'object') {
    return items[key] || ''
  }
  
  return ''
}

/**
 * Reproduction exacte de formatDate de Epic/Lot.js via ../Util
 */
export const formatDate = (date) => {
  if (!date) return ''
  return moment(date).format('DD/MM/YYYY')
}

/**
 * Reproduction exacte de isVisitableProperty de ../Util
 * Une propriété est visitable si le statut est null/undefined OU si c'est un nombre OU si c'est "vacant"
 */
export const isVisitableProperty = (scheduledVisitStatus) => {
  // Logique exacte de l'ancienne app React: isNil(status) || !isNaN(status)
  // Ajout du cas "vacant" qui semble être un statut visitable dans les données
  return scheduledVisitStatus == null || !isNaN(scheduledVisitStatus) || scheduledVisitStatus === 'vacant'
}

/**
 * Fonction utilitaire pour nettoyer les virgules (stripComa de Epic/Lot.js)
 */
const stripComa = (str) => {
  return (str || '').replace(/,/g, '')
}

/**
 * Reproduction exacte de formatAddress de Epic/Lot.js
 */
export const formatAddress = (address) => {
  if (!address) return ''
  
  const { street, street2, zipCode, city } = address
  
  // Première partie: construire la rue
  let fullStreet = `${stripComa(street) || ''}${street2 ? stripComa(street2) + ', ' : ', '}`
  
  // Si c'est juste une virgule, on la vide
  if (fullStreet.trim() === ',') {
    fullStreet = ''
  }
  
  // Construire l'adresse complète
  const fullAddress = `${fullStreet}${zipCode ? zipCode + ' ' : ''}${city || ''}`
  
  return fullAddress.trim()
}

/**
 * Reproduction exacte de formatPhoneOwner de Epic/Lot.js
 */
export const formatPhoneOwner = (owner) => {
  if (!owner) return {}
  
  const { phoneNumber, mobilePhoneNumber, officePhoneNumber, ...rest } = owner
  
  return {
    ...rest,
    phones: [
      { type: 'home', number: phoneNumber },
      { type: 'mobile', number: mobilePhoneNumber },
      { type: 'office', number: officePhoneNumber },
    ],
  }
}

/**
 * Reproduction exacte de withDefaultTechnicalManager de Epic/Lot.js
 */
export const withDefaultTechnicalManager = (technicalManager) => {
  return technicalManager || { id: '', name: '', team: {} }
}

/**
 * Reproduction exacte de withDefaultParent de Epic/Lot.js
 */
export const withDefaultParent = (parent) => {
  return parent || { id: '', nsil: '' }
}

/**
 * Reproduction exacte de lastOccupantPanel de Epic/Lot.js
 */
export const lastOccupantPanel = (occupant) => ({
  title: 'Dernier occupant',
  people: occupant.name,
  phones: [
    { type: 'mobile', number: occupant.mobilePhoneNumber },
    { type: 'home', number: occupant.phoneNumber },
    { type: 'office', number: occupant.officePhoneNumber },
  ],
  mail: occupant.email,
  panes: [
    { title: 'Catégorie du bail', value: occupant.leaseCategory },
    { title: 'Date d\'entrée', value: formatDate(occupant.arrivedAt) },
    { title: 'Date de sortie', value: formatDate(occupant.exitedAt) },
  ],
})

/**
 * Reproduction exacte de ownerPanel de Epic/Lot.js
 */
export const ownerPanel = (owner) => ({
  title: 'Propriétaire',
  people: owner.name,
  address: formatAddress(owner.address),
  phones: [
    { type: 'mobile', number: owner.mobilePhoneNumber },
    { type: 'home', number: owner.phoneNumber },
    { type: 'office', number: owner.officePhoneNumber },
  ],
  mail: owner.email,
  panes: [],
})

/**
 * Reproduction exacte de managerPanel de Epic/Lot.js
 */
export const managerPanel = (manager) => ({
  title: 'Gestion Propriétaire',
  people: manager.mandator.name,
  address: formatAddress(manager.mandator.address),
  phones: [
    { type: 'mobile', number: manager.mandator.mobilePhoneNumber },
    { type: 'home', number: manager.mandator.phoneNumber },
    { type: 'office', number: manager.mandator.officePhoneNumber },
  ],
  mail: manager.mandator.email,
  panes: [
    { title: 'Date de début de gestion', value: formatDate(manager.startedAt) },
    { title: 'Date de fin de gestion', value: formatDate(manager.endedAt) },
    { title: 'Date d\'état de gestion', value: formatDate(manager.statusUpdatedAt) },
    { title: 'État de gestion', value: manager.status },
    { title: 'Code immeuble', value: manager.propertyCode },
    { title: 'Code lot', value: manager.unitCode },
  ],
})

/**
 * Reproduction exacte de assistancePanel de Epic/Lot.js
 */
export const assistancePanel = (property) => (units) => (subUnits) => (assistance) => ({
  title: 'Convention d\'assistance',
  people: assistance.mandator.name,
  address: formatAddress(assistance.mandator.address),
  mail: assistance.mandator.email,
  phones: [
    { type: 'mobile', number: assistance.mandator.mobilePhoneNumber },
    { type: 'home', number: assistance.mandator.phoneNumber },
    { type: 'office', number: assistance.mandator.officePhoneNumber },
  ],
  panes: [
    { title: 'Date de début de gestion', value: formatDate(assistance.startedAt) },
    { title: 'Date de fin de gestion', value: formatDate(assistance.endedAt) },
    { title: 'Date d\'état de gestion', value: formatDate(assistance.statusUpdatedAt) },
    { title: 'État de gestion', value: assistance.status },
    { title: 'Code immeuble', value: assistance.propertyCode },
    { title: 'Code lot', value: assistance.unitCode },
    { title: 'Unité', value: itemValue(property.businessUnit, units) },
    { title: 'Sous-unité', value: itemValue(property.businessSubUnit, subUnits) },
  ],
})

/**
 * Reproduction exacte de mapApiDataToState de Epic/Lot.js
 */
export const mapApiDataToState = (apiData) => (propertyTypes) => (units) => (subUnits) => (visitStatuses) => {
  const panels = [
    { data: apiData.assistance, formater: assistancePanel(apiData)(units)(subUnits) },
    { data: apiData.manager, formater: managerPanel },
    { data: apiData.legalOwner, formater: ownerPanel },
    { data: apiData.lastOccupant, formater: lastOccupantPanel },
  ]
    .filter(({ data }) => data && data.id !== '')
    .map(({ data, formater }) => formater(data))

  return {
    ...apiData,
    type: itemValue(apiData.type, propertyTypes),
    lastOccupant: formatPhoneOwner(apiData.lastOccupant || {}),
    assistance: formatPhoneOwner(apiData.assistance || {}),
    owner: formatPhoneOwner(apiData.legalOwner || {}),
    manager: formatPhoneOwner(apiData.manager || {}),
    scheduledVisitAt: formatDate(apiData.scheduledVisitAt),
    scheduledVisitStatus: apiData.scheduledVisitStatus
      ? itemValue(apiData.scheduledVisitStatus, visitStatuses)
      : '',
    planifiedAt: formatDate(apiData.planifiedAt),
    technicalManager: withDefaultTechnicalManager(apiData.technicalManager),
    parent: apiData.relatedParent && apiData.relatedParent.nsil,
    parentId: apiData.relatedParent && apiData.relatedParent.id,
    panels,
  }
}

/**
 * Récupère la valeur d'un statut de visite via le dictionnaire
 */
export const getVisitStatusValue = (scheduledVisitStatus, visitStatuses) => {
  if (!scheduledVisitStatus) return ''
  return itemValue(scheduledVisitStatus, visitStatuses)
}

/**
 * Récupère la valeur d'un type de propriété via le dictionnaire
 */
export const getPropertyTypeValue = (typeCode, propertyTypes) => {
  if (!typeCode) return 'Non défini'
  const value = itemValue(typeCode, propertyTypes)
  return value || typeCode || 'Non défini'
}

/**
 * Génère la colonne "A visiter avant" selon la logique exacte de Epic/List.js
 * IMPORTANT: Cette logique ne dépend PAS de isVisitableProperty !
 */
export const getScheduledVisitColumn = (item, visitStatuses) => {
  // Logique exacte de Epic/List.js lignes 665-669
  // property.scheduledVisitStatus
  //   ? [ { type: 'ScheduledAt', value: itemValue(property.scheduledVisitStatus)(visitStatuses) } ]
  //   : property.scheduledVisitAt
  //     ? [ { type: 'ScheduledAt', value: formatDate(property.scheduledVisitAt) } ]
  //     : [],
  
  if (item.scheduledVisitStatus) {
    // Utiliser la syntaxe curryfiée exacte de l'ancienne app React
    const transformedValue = itemValue(item.scheduledVisitStatus)(visitStatuses)
    // Seulement retourner si la transformation a donné un résultat valide
    if (transformedValue && transformedValue !== '') {
      return [{ type: 'ScheduledAt', value: transformedValue }]
    }
  }
  
  if (item.scheduledVisitAt) {
    const formattedDate = formatDate(item.scheduledVisitAt)
    return [{ type: 'ScheduledAt', value: formattedDate }]
  }
  
  return []
}

/**
 * Génère la colonne "Attribution visite" selon la logique exacte de Epic/List.js
 * Adaptée pour utiliser le composant AttributionVisite
 */
export const getAttributionVisiteColumn = (item) => {
  if (isVisitableProperty(item.scheduledVisitStatus)) {
    if (item.technicalManager && item.technicalManager.id) {
      // Cas où une personne est attribuée - utiliser le composant avec les données complètes
      return [
        {
          type: 'Attribute',
          value: item.technicalManager
        },
        { type: 'Text', value: getDelegationText(item) }
      ]
    } else {
      // Cas où personne n'est attribué - utiliser le composant avec null
      return [
        { type: 'Attribute', value: null }
      ]
    }
  }
  return []
}

/**
 * Génère la colonne "Planifiée le" selon la logique exacte de Epic/List.js
 * Retourne le type 'Planified' si une date existe, sinon 'Planify'
 */
export const getPlanifiedColumn = (item) => {
  if (isVisitableProperty(item.scheduledVisitStatus)) {
    if (item.planifiedAt) {
      // Si une date de planification existe, afficher le DatePickerInput avec la date
      return [{ type: 'Planified', value: item.planifiedAt }]
    } else {
      // Sinon, afficher le DatePickerInput sans date (placeholder "À planifier")
      return [{ type: 'Planify', value: null }]
    }
  }
  return []
}

/**
 * Génère le texte de délégation pour une propriété
 * Utilise la comparaison par nom d'équipe (plus robuste que par ID)
 */
export const getDelegationText = (item) => {
  if (!item || !item.teams || item.teams.length === 0) {
    return ''
  }
  
  const managerTeam = item.technicalManager?.team
  if (!managerTeam || !managerTeam.name) {
    return ''
  }
  
  // Vérifier si le nom de l'équipe du gestionnaire technique est dans les noms des équipes de la propriété
  const isManagerTeamInPropertyTeams = item.teams.some(team => 
    team && team.name && team.name === managerTeam.name
  )
  
  // Si l'équipe du gestionnaire EST dans les équipes de la propriété = PAS de délégation
  if (isManagerTeamInPropertyTeams) {
    return ''
  }
  
  // Si l'équipe du gestionnaire n'est PAS dans les équipes de la propriété = délégation
  const firstTeamName = item.teams[0].name
  const formattedTeamName = firstTeamName.replace(/Groupe/i, 'gpe')
  const additionalTeams = item.teams.length > 1 ? ', ...' : ''
  
  return `Délégation ${formattedTeamName}${additionalTeams}`
}
